/**
 * RuntimeFormDrawer
 *
 * Workspace-anchored overlay for create/edit flows. Reads URL search params:
 *
 *   ?form=<context>&mode=<create|edit>&id=<entityId?>&hospitalId=…&hcpId=…
 *
 * When `form` and `mode` are present, slides in a right-side Sheet rendering
 * the same RuntimeFormBody used by the standalone runtime page. Closing the
 * drawer (overlay click, Esc, Cancel button, or successful submit) clears
 * the runtime query params via setSearchParams — the user stays on the
 * /workspace path with workspace state intact.
 *
 * Mounted once inside WorkspaceLayout so it is available across all
 * workspace sub-routes.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { Sheet, SheetContent } from '@/modules/shared/components/ui/sheet';

import {
  CONTEXT_MAP,
  isValidContext,
  type RuntimeContextKey,
} from '../../runtime/contextMap';
import { RuntimeFormBody } from '../../pages/runtime/RuntimeFormBody';

/** Query params reserved by the runtime drawer. */
const DRAWER_PARAMS = ['form', 'mode', 'id'] as const;

interface RuntimeFormDrawerProps {
  programId: string;
}

export function RuntimeFormDrawer({ programId }: RuntimeFormDrawerProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const formParam = searchParams.get('form') ?? undefined;
  const modeParam = searchParams.get('mode') ?? undefined;
  const entityId = searchParams.get('id') ?? undefined;

  const contextKey = formParam as RuntimeContextKey | undefined;
  const validContext = isValidContext(contextKey);
  const config = validContext ? CONTEXT_MAP[contextKey!] : null;

  const isOpen = !!formParam && !!modeParam;

  // Parent IDs sourced from the same query string the page route uses.
  const parentIds = useMemo(() => {
    const out: Record<string, string> = {};
    config?.parentParams.forEach((key) => {
      const val = searchParams.get(key);
      if (val) out[key] = val;
    });
    return out;
  }, [config, searchParams]);

  // Strip drawer params + parent param hints, keep everything else (e.g.
  // workspace tab/filter state).
  const closeDrawer = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    DRAWER_PARAMS.forEach((k) => next.delete(k));
    config?.parentParams.forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, config]);

  // Refetch workspace data after a successful submit.
  const handleSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['workspace'] });
  }, [queryClient]);

  // Lock body scroll while drawer is open.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) closeDrawer();
      }}
    >
      <SheetContent
        side="right"
        hideClose
        className="w-full sm:max-w-2xl h-full p-0 flex flex-col gap-0 overflow-hidden"
      >
        <RuntimeFormBody
          programId={programId}
          contextRaw={formParam}
          modeRaw={modeParam}
          entityId={entityId}
          parentIds={parentIds}
          onClose={closeDrawer}
          onSuccess={handleSuccess}
          compact
        />
      </SheetContent>
    </Sheet>
  );
}

/**
 * Build a workspace URL that opens the runtime drawer.
 *
 * @example
 *   navigate(buildRuntimeDrawerUrl(programId, 'hospital', 'create'));
 *   navigate(buildRuntimeDrawerUrl(programId, 'patient', 'edit', patientId,
 *     { hospitalId, hcpId }));
 */
export function buildRuntimeDrawerUrl(
  programId: string,
  context: RuntimeContextKey | string,
  mode: 'create' | 'edit',
  entityId?: string,
  parentIds?: Record<string, string | undefined>,
): string {
  const params = new URLSearchParams();
  params.set('form', String(context));
  params.set('mode', mode);
  if (entityId) params.set('id', entityId);
  if (parentIds) {
    Object.entries(parentIds).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
  }
  return `/programs/${programId}/workspace?${params.toString()}`;
}
